import React, { useCallback, useMemo } from 'react';
import { Table, SearchBar, Dropdown, DropdownTrigger, DropdownContent, DropdownItem } from 'equal-ds-ui';
import type { TableColumn } from 'equal-ds-ui';
import {
  Check,
  Edit3,
  Power,
  Ban,
  Unlock,
  MoreVertical,
  Key,
} from 'lucide-react';
import type {
  AdminUserPermissions,
  AdminUserSearchField,
  AdminUserSearchFormState,
  AdminUserTableRow,
} from '../../../types/admin-users';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import { CopyButton } from '../../ui/CopyButton';

export interface AdminUsersTableProps {
  rows: AdminUserTableRow[];
  isLoading: boolean;
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onEdit: (row: AdminUserTableRow) => void;
  onToggleStatus: (row: AdminUserTableRow) => void;
  onResetPassword: (row: AdminUserTableRow) => void;
  onApprove: (row: AdminUserTableRow) => void;
  onReject: (row: AdminUserTableRow) => void;
  permissions: AdminUserPermissions;
  onCreateRequested?: () => void;
  searchForm: AdminUserSearchFormState;
  onSearchFieldChange: (field: AdminUserSearchField) => void;
  onSearchValueChange: (value: string) => void;
  onSearchSubmit: (value: string, field: AdminUserSearchField) => void;
  onSearchReset: () => void;
}

export const AdminUsersTable: React.FC<AdminUsersTableProps> = ({
  rows,
  isLoading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onToggleStatus,
  onResetPassword,
  onApprove,
  onReject,
  permissions,
  //onCreateRequested,
  searchForm,
  onSearchFieldChange,
  onSearchValueChange,
  onSearchSubmit,
  onSearchReset,
}) => {
  const searchOptions: Array<{ value: AdminUserSearchField; label: string }> = useMemo(
    () => [
      { value: 'name', label: 'Name of the User' },
      { value: 'email', label: 'Email ID' },
      { value: 'mobile', label: 'Mobile' },
      { value: 'emp_id', label: 'Employee ID' },
      { value: 'dept_code', label: 'Department Code' },
    ],
    []
  );

  const handleSearch = useCallback(
    (query: string, domain?: string) => {
      const trimmed = query.trim();
      const nextField = (domain ?? searchForm.searchField) as AdminUserSearchField;

      onSearchFieldChange(nextField);
      onSearchValueChange(query);

      if (trimmed.length === 0) {
        onSearchReset();
      } else {
        onSearchSubmit(trimmed, nextField);
      }
    },
    [onSearchFieldChange, onSearchReset, onSearchSubmit, onSearchValueChange, searchForm.searchField]
  );

  // Create a lookup map to get original row with 'raw' property by id
  const rowsById = useMemo(() => {
    const map = new Map<string, AdminUserTableRow>();
    rows.forEach((row) => map.set(row.id, row));
    return map;
  }, [rows]);

  const columns = useMemo<TableColumn<AdminUserTableRow>[]>(() => {
    const baseColumns: TableColumn<AdminUserTableRow>[] = [
      {
        key: 'email',
        header: 'Email',
        width: 220,
        accessor: (row) => row.email,
        render: (value) => (
          <div className="flex items-center gap-1.5">
            <span
              className="text-sm font-mono truncate flex-1 min-w-0"
              title={value as string}
            >
              {value || '--'}
            </span>
            {value && <CopyButton text={value as string} className="flex-shrink-0 text-gray-700 hover:text-gray-900" />}
          </div>
        ),
      },
      {
        key: 'mobile',
        header: 'Mobile',
        width: 140,
        accessor: (row) => row.mobile,
        render: (value) => (
          <div className="flex items-center gap-1.5">
            <span className="text-sm truncate flex-1 min-w-0" title={value as string}>
              {value || '--'}
            </span>
            {value && <CopyButton text={value as string} className="flex-shrink-0 text-gray-700 hover:text-gray-900" />}
          </div>
        ),
      },
      {
        key: 'empId',
        header: 'Employee ID',
        width: 120,
        accessor: (row) => row.empId,
        render: (value) => <span className="text-sm">{value || '--'}</span>,
      },
      {
        key: 'name',
        header: 'Employee Name',
        width: 180,
        accessor: (row) => row.name,
        render: (value, row) => {
          const isPendingApproval = row.status === 'PENDING_APPROVAL';
          return (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary">{value || '--'}</span>
              {isPendingApproval && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-900 text-white">
                  New
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'deptCode',
        header: 'Business Dept Code',
        width: 150,
        accessor: (row) => row.deptCode,
        render: (value) => <span className="text-sm">{value || '--'}</span>,
      },
      {
        key: 'roleStatus',
        header: 'Admin',
        width: 100,
        accessor: (row) => row.roleStatus,
        render: (value) => (
          <span className={`text-sm font-medium ${value === 'Yes' ? 'text-green-600' : 'text-text-secondary'}`}>
            {value || '--'}
          </span>
        ),
      },
      {
        key: 'roleName',
        header: 'Role',
        width: 140,
        accessor: (row) => row.roleName,
        render: (value, row) => {
          const isPendingApproval = row.status === 'PENDING_APPROVAL';
          if (isPendingApproval) {
            return (
              <span className="text-sm text-text-secondary italic">Awaiting Approval...</span>
            );
          }
          return <span className="text-sm">{value || '--'}</span>;
        },
      },
      {
        key: 'createdAt',
        header: 'Timestamp',
        width: 180,
        accessor: (row) => row.createdAt,
        render: (value) => <span className="text-sm text-text-secondary">{value || '--'}</span>,
      },
      {
        key: 'createdBy',
        header: 'User Created By',
        width: 140,
        accessor: (row) => row.createdBy,
        render: (value) => <span className="text-sm">{value || '--'}</span>,
      },
      {
        key: 'status',
        header: 'Account Status',
        width: 240,
        accessor: (row) => row.status,
        render: (value, row) => {
          const isPendingApproval = value === 'PENDING_APPROVAL';

          // Status badge colors using inline styles for reliability
          const statusStyles: Record<string, React.CSSProperties> = {
            ACTIVE: { backgroundColor: '#dcfce7', color: '#15803d' }, // green
            INACTIVE: { backgroundColor: '#fef9c3', color: '#a16207' }, // yellow
            LOCKED: { backgroundColor: '#fee2e2', color: '#dc2626' }, // red
            PENDING_APPROVAL: { backgroundColor: '#dbeafe', color: '#1d4ed8' }, // blue
            DEFAULT: { backgroundColor: '#f3f4f6', color: '#374151' }, // gray
          };

          const badgeStyle = statusStyles[value as string] || statusStyles.DEFAULT;

          // Status toggle button config
          const statusAction = String((row.raw as any)?.user_status ?? 'Update');

          const buttonStyles: Record<string, React.CSSProperties> = {
            ACTIVE: { backgroundColor: '#eab308' }, // yellow - Deactivate
            INACTIVE: { backgroundColor: '#22c55e' }, // green - Activate
            LOCKED: { backgroundColor: '#ef4444' }, // red - Unlock
          };

          const buttonStyle = buttonStyles[value as string] || buttonStyles.ACTIVE;
          const StatusIcon = value === 'LOCKED' ? Unlock : Power;

          return (
            <div className="flex items-center gap-3">
              <span
                style={{
                  ...badgeStyle,
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 10px',
                  borderRadius: '9999px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {value || '--'}
              </span>
              {permissions.canToggleStatus && !isPendingApproval && (
                <button
                  onClick={() => onToggleStatus(row)}
                  style={{
                    ...buttonStyle,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  <StatusIcon style={{ width: '14px', height: '14px' }} />
                  {statusAction}
                </button>
              )}
            </div>
          );
        },
      },
    ];

    // Actions column with 3-dot dropdown for Edit and Reset Password
    const actionsColumn: TableColumn<AdminUserTableRow> = {
      key: 'actions',
      header: 'Actions',
      width: 140,
      align: 'center',
      isActions: true, // Makes column sticky on the right side
      locked: true, // Prevents reordering/hiding
      render: (_value, row) => {
        // Get the original row with 'raw' property for callbacks
        const originalRow = rowsById.get(row.id) ?? row;
        const isPendingApproval = row.status === 'PENDING_APPROVAL';

        // For pending approval users, show Approve/Reject buttons directly
        if (isPendingApproval) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
              {permissions.canApproveUser && (
                <button
                  onClick={() => onApprove(originalRow)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: '#22c55e',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#16a34a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#22c55e';
                  }}
                >
                  <Check style={{ width: '12px', height: '12px' }} />
                  Approve
                </button>
              )}
              {permissions.canRejectUser && (
                <button
                  onClick={() => onReject(originalRow)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ef4444';
                  }}
                >
                  <Ban style={{ width: '12px', height: '12px' }} />
                  Reject
                </button>
              )}
            </div>
          );
        }

        // Check if there are any actions available
        const hasEditPermission = permissions.canEditUser;
        // Don't show reset password for protected roles (admin, superadmin)
        // Use role_name (not display_name) to match Angular behavior exactly
        // Angular: element.user_profile.roles.role_name == 'superadmin' || element.user_profile.roles.role_name == 'admin'
        const PROTECTED_ROLES = new Set(['admin', 'superadmin']);
        const rawRoleName = (originalRow.raw?.user_profile?.roles?.role_name ?? '').toLowerCase();
        const isProtectedRole = PROTECTED_ROLES.has(rawRoleName);
        const hasResetPasswordPermission = permissions.canResetPassword && !isProtectedRole;

        if (!hasEditPermission && !hasResetPasswordPermission) {
          return <span className="text-xs text-text-secondary">--</span>;
        }

        // 3-dot dropdown menu for non-pending users
        return (
          <Dropdown>
            <DropdownTrigger asChild>
              <button
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
                aria-label="More actions"
              >
                <MoreVertical className="w-5 h-5 text-gray-600" />
              </button>
            </DropdownTrigger>
            <DropdownContent
              zIndex={9999}
              align="end"
              className="min-w-[160px] p-1 bg-white rounded-lg shadow-lg border border-gray-200"
            >
              {hasEditPermission && (
                <DropdownItem
                  onSelect={() => onEdit(originalRow)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md cursor-pointer transition-colors"
                >
                  <Edit3 className="w-4 h-4 text-gray-500" />
                  <span>Edit User</span>
                </DropdownItem>
              )}
              {hasResetPasswordPermission && (
                <DropdownItem
                  onSelect={() => onResetPassword(originalRow)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md cursor-pointer transition-colors"
                >
                  <Key className="w-4 h-4 text-gray-500" />
                  <span>Reset Password</span>
                </DropdownItem>
              )}
            </DropdownContent>
          </Dropdown>
        );
      },
    };

    return [...baseColumns, actionsColumn];
  }, [permissions, onApprove, onEdit, onReject, onResetPassword, onToggleStatus, rowsById]);

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-sm bg-white">
      <div className="flex md:flex-row md:items-center md:justify-end gap-3 mb-4">
        <div className="w-full md:w-auto md:min-w-[320px]">
          <SearchBar
            variant="with-dropdown"
            domains={searchOptions}
            value={searchForm.searchValue}
            onChange={onSearchValueChange}
            selectedDomain={searchForm.searchField}
            onDomainChange={(domain) => onSearchFieldChange(domain as AdminUserSearchField)}
            onSearch={handleSearch}
            onClear={onSearchReset}
            placeholder="Search admin users"
            disabled={isLoading}
          />
        </div>
      </div>

      <Table
        className="flex-1 min-h-0"
        data={rows.map(({ raw, ...rest }) => rest) as any}
        columns={columns}
        loading={isLoading}
        pagination={{
          currentPage: pagination.page,
          pageSize: pagination.pageSize,
          totalItems: pagination.totalItems,
          onPageChange,
          onPageSizeChange,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />
    </div>
  );
};
